import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, MessageSquare, Target, Building2, Users } from 'lucide-react';

const WorkWithMe: React.FC = () => {
  return (
    <div className="space-y-24 pb-24 bg-orange-50">
       {/* Header */}
      <section className="bg-stone-900 text-orange-50 py-24 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <span className="uppercase tracking-widest text-sm font-medium text-orange-400">Services</span>
          <h1 className="text-5xl md:text-6xl font-serif">WORK WITH ME</h1>
          <p className="text-xl text-orange-100/80 leading-relaxed">
            Choose the level of support that fits your current business stage or organizational goals. 
            Whether you’re an individual exploring entrepreneurship or an institution looking to empower your students, 
            I help turn skills and experience into a business that fits real life.
          </p>
        </div>
      </section>

      {/* Ways to Work Together */}
      <section className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Ways to Work Together</h2>
        
        {/* Top Row: Individuals & Consultation */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Option 1: Individuals */}
            <div className="bg-white p-8 rounded-xl border border-orange-100 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mb-6 text-brand-dark">
                    <Target size={24} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-stone-900 mb-2">1. The 90-Day Business Growth Program</h3>
                <p className="text-sm font-bold text-orange-600 mb-4 uppercase tracking-wider">For Individuals</p>
                <p className="text-stone-600 mb-6">A structured coaching journey to help you:</p>
                <ul className="space-y-3 mb-8 flex-grow">
                    {['Identify a profitable niche', 'Create an offer people want', 'Build a professional digital presence', 'Launch your Minimum Viable Offer'].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-700 text-sm">
                            <CheckCircle size={16} className="text-orange-500 mt-0.5 shrink-0" />
                            {item}
                        </li>
                    ))}
                </ul>
                <div className="pt-6 border-t border-orange-100 mt-auto">
                    <p className="text-sm font-medium text-brand-dark mb-4">👉 Best for women who want accountability and a clear roadmap.</p>
                     <Link to="/program" className="inline-flex items-center gap-2 text-stone-900 font-bold hover:text-orange-600 transition-colors">
                        View Program Details <ArrowRight size={16}/>
                     </Link>
                </div>
            </div>

            {/* Option 3: Free Consultation (Renamed/Moved to maintain balance) */}
            <div className="bg-stone-900 text-orange-50 p-8 rounded-xl shadow-lg flex flex-col">
                <div className="bg-white/10 w-12 h-12 rounded-full flex items-center justify-center mb-6 text-white">
                    <MessageSquare size={24} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-2">2. Free Consultation</h3>
                 <p className="text-sm font-bold text-orange-400 mb-4 uppercase tracking-wider">For Everyone</p>
                <p className="text-orange-100/80 mb-6 text-lg flex-grow">
                    Not sure where to start? <br/>
                    Let’s talk through your situation, whether you're an individual or representing an organization, and see what support makes sense.
                </p>
                <div className="mt-auto pt-6 border-t border-stone-700">
                     <Link to="/contact" className="w-full bg-orange-500 text-white text-center py-3 rounded-md font-bold hover:bg-orange-600 transition-colors inline-block">
                        Reach out to us
                     </Link>
                </div>
            </div>
        </div>

        {/* Option 2: Institutional Partnerships (Full Width Feature) */}
        <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden">
             <div className="bg-orange-100 p-8 md:p-10 border-b border-orange-200">
                <div className="flex items-center gap-4 mb-4">
                    <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center text-brand-dark shadow-sm shrink-0">
                        <Building2 size={24} />
                    </div>
                    <div>
                        <h3 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">3. Institutional & Training Partnerships</h3>
                        <p className="text-stone-600 font-medium">Skills-to-business workshops for students and professionals.</p>
                    </div>
                </div>
             </div>
             
             <div className="p-8 md:p-10 grid md:grid-cols-2 gap-12">
                 {/* Left Column */}
                 <div className="space-y-8">
                     <div>
                         <p className="text-lg text-stone-800 mb-4 font-medium">
                            I partner with training institutes, academies, and professional programs to support students who want to:
                         </p>
                         <ul className="space-y-2">
                            {['Turn newly learned skills into income', 'Build service-based businesses with clarity', 'Develop real-world offers, not just certificates', 'Transition confidently from learning to earning'].map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-stone-700">
                                    <CheckCircle size={18} className="text-orange-500 mt-1 shrink-0" />
                                    {item}
                                </li>
                            ))}
                         </ul>
                     </div>

                     <div>
                        <h4 className="font-bold text-stone-900 mb-3 uppercase tracking-wide text-sm border-b border-orange-100 pb-2">Why Partner With Productive Folks</h4>
                        <ul className="space-y-3">
                            <li className="flex gap-2 text-stone-700">
                                <span className="text-orange-500 font-bold">✓</span> Practical, outcome-focused training
                            </li>
                            <li className="flex gap-2 text-stone-700">
                                <span className="text-orange-500 font-bold">✓</span> Designed for beginners and career switchers
                            </li>
                             <li className="flex gap-2 text-stone-700">
                                <span className="text-orange-500 font-bold">✓</span> Done-With-You hands-on program
                            </li>
                             <li className="flex gap-2 text-stone-700">
                                <span className="text-orange-500 font-bold">✓</span> Led by an experienced corporate trainer and coach
                            </li>
                        </ul>
                     </div>
                 </div>

                 {/* Right Column */}
                 <div className="space-y-8">
                     <div className="bg-stone-50 p-6 rounded-lg border border-stone-100">
                        <h4 className="font-bold text-stone-900 mb-4 flex items-center gap-2">
                            <Users size={18} className="text-brand-dark"/> Partnership Options May Include
                        </h4>
                        <ul className="space-y-2 mb-6">
                            {['Guest workshops or masterclasses', 'Short-term business clarity programs', 'Cohort-based 90-day business support', 'Post-training entrepreneurship pathways for students'].map((item, i) => (
                                <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 bg-stone-400 rounded-full mt-1.5"></span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-stone-500 italic">
                            Programs are customized based on student level, program duration, and desired outcomes.
                        </p>
                     </div>

                     <div>
                        <h4 className="font-bold text-stone-900 mb-3 uppercase tracking-wide text-sm border-b border-orange-100 pb-2">Who This Is For</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-stone-700">
                            <span>✔ Training institutes</span>
                            <span>✔ Professional certifications</span>
                            <span>✔ Skill-based bootcamps</span>
                            <span>✔ Career transition programs</span>
                        </div>
                     </div>

                     <div className="pt-4">
                        <div className="bg-stone-900 text-white p-6 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div>
                                <p className="font-bold text-lg mb-1">Next Step</p>
                                <p className="text-orange-100/80 text-sm">Interested in offering your students a clear path from skills to income?</p>
                            </div>
                            <Link to="/contact" className="bg-white text-stone-900 px-5 py-2.5 rounded font-bold hover:bg-orange-100 transition-colors whitespace-nowrap">
                                Explore a Partnership
                            </Link>
                        </div>
                     </div>
                 </div>
             </div>
        </div>
      </section>
    </div>
  );
};

export default WorkWithMe;